import { Divider } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { Container, Grid } from "@mui/material";
import axios from "axios";
import {
  AutoCompleteComponent,
  CustomTable,
  DatePicker,
  FormCard,
  FormLabel,
  ModalError,
  ModalSuccess,
  ModalTable,
  NumberInput,
  Page,
  PrimaryButton,
  SectionLabel,
  SkeletonComponent,
  StatusChip,
  TextInput,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import moment from "moment-timezone";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import {
  formatDate,
  formatNpwp,
  formatPayloadDate,
  getErrors,
  MASKS,
} from "utils";
import ModalComposerAliasName from "./Components/ModalComposerAliasName";
import { debounce } from "lodash";

const FormParameterComposer = ({ userLogin }) => {
  const classes = globalStyles();
  const history = useHistory();
  const { id } = useParams();
  const isFirstLoad = useRef(true);
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const pageTitle = `${id ? "Edit" : "Add"} Composer/Author`;

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingTableModal, setLoadingTableModal] = useState(false);
  const [optionTax, setOptionTax] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
  });
  const [payload, setPayload] = useState({
    sure_name: "",
    email: "",
    phone_number: "",
    alias_names: [],
    npwp: "",
    identification_number: "",
    address: "",
    ipi_name_number: "",
    ipi_base_number: "",
    identification_type: "",
    composer_tax_id: "",
    composer_publisher: [
      {
        composer_publisher_id: 0,
        publisher_id: userLogin?.publisher?.publisher_id || 0,
        contract_number: "",
        contract_start_period: moment(),
        contract_end_period: moment(),
        share_percentage: 0,
      },
    ],
    ...(id && { song_composer: [] }),
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [preloadAliasName, setPreloadAliasName] = useState(null);
  const [dataTable, setDataTable] = useState([]);
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [modalTableVisible, setModalTableVisible] = useState(false);
  const [dataTableModal, setDataTableModal] = useState([]);
  const [tableModalTotalPage, setTableModalTotalPage] = useState(1);
  const [tableModalParams, setTableModalParams] = useState({
    page: 1,
    size: 10,
    search: "",
  });

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangePayload = (value, key) => {
    setPayload(currentState => ({
      ...currentState,
      [key]: value,
    }));
  };
  const handleChangeTableModalParams = (value, key) => {
    setTableModalParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleAddAliasName = (name, index) => {
    setPayload(prev => ({
      ...prev,
      alias_names: index
        ? prev.alias_names.map((item, idx) => (idx === index ? name : item))
        : [...prev.alias_names, name],
    }));
  };
  const handleDeleteAliasName = index => {
    setPayload(prev => ({
      ...prev,
      alias_names: (prev?.alias_names || []).filter((_, idx) => idx !== index),
    }));
  };
  const handleChangeComposerPublisher = (value, key, id = 0) => {
    setPayload(prev => ({
      ...prev,
      composer_publisher: prev.composer_publisher.map(item =>
        item.composer_publisher_id === id ? { ...item, [key]: value } : item
      ),
    }));
  };
  const handleChangeStartDate = date => {
    const endDate = payload?.composer_publisher?.[0]?.contract_end_period;
    if (endDate && moment(date).isAfter(endDate)) {
      handleChangeComposerPublisher(date, "contract_end_period");
    }
    handleChangeComposerPublisher(date, "contract_start_period");
  };
  const handleChangeEndDate = date => {
    const startDate = payload?.composer_publisher?.[0]?.contract_start_period;
    if (startDate && moment(date).isBefore(startDate)) {
      handleChangeComposerPublisher(date, "contract_start_period");
    }
    handleChangeComposerPublisher(date, "contract_end_period");
  };
  const handleChangeComposerPercentage = (value, song) => {
    const percentage = Number(value);
    setDataTable(prevDataTable =>
      prevDataTable.map(item =>
        item?.song_composer_id === song?.song_composer_id
          ? { ...item, composer_share_percentage: percentage }
          : item
      )
    );
    setPayload(prev => {
      const updatedComposer = {
        ...song,
        composer_share_percentage: percentage,
      };

      const exists = prev.song_composer?.some(
        item => item.song_composer_id === song.song_composer_id
      );

      return {
        ...prev,
        song_composer: exists
          ? prev.song_composer.map(item =>
              item.song_composer_id === song.song_composer_id
                ? updatedComposer
                : item
            )
          : [...(prev.song_composer || []), updatedComposer],
      };
    });
  };

  const getOptionTax = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/transaction/v2/tax/composer-tax`,
        { headers }
      );
      const modifiedData = res?.data?.data?.map(item => ({
        ...item,
        label: `${item?.code} (${item?.rate}%)`,
      }));
      setOptionTax(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getDetail = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/publisher/composer/${id}`, {
        headers,
      });
      const { data } = res?.data;
      const requiredKeys = [
        "sure_name",
        "email",
        "phone_number",
        "alias_names",
        "identification_number",
        "address",
        "ipi_name_number",
        "ipi_base_number",
        "identification_type",
        "composer_publisher",
        "contracts",
        "composer_tax_id",
      ];
      const filteredData = requiredKeys?.reduce((acc, key) => {
        acc[key] = data[key];
        return acc;
      }, {});
      setPayload({ ...filteredData, npwp: formatNpwp(data?.npwp) });
      getDataTable(data?.sure_name);
      isFirstLoad.current = false;
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getDataTable = async sure_name => {
    try {
      setLoadingTable(true);
      const res = await axios.get(`${hardBaseUrl}/composers/${id}/song-list`, {
        headers,
        params: queryParams,
      });
      const { data, meta } = res?.data;
      const modifiedData = data?.map(item => {
        const modified = payload.song_composer?.find(
          sc => sc.song_composer_id === item.song_composer_id
        );
        return {
          ...item,
          composer_name: item.composer_name || sure_name,
          composer_share_percentage: modified
            ? modified.composer_share_percentage
            : item.composer_share_percentage,
        };
      });

      setDataTable(modifiedData || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingTable(false);
    }
  };
  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [getOptionTax()];
    if (id) {
      promises?.push(getDetail());
    }
    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      setLoadingPage(false);
    }
  };
  const handleSubmit = async () => {
    const method = id ? axios.put : axios.post;
    const requiredKeys = [
      "sure_name",
      "email",
      "identification_number",
      "address",
      "alias_names",
      "ipi_name_number",
      "ipi_base_number",
      "identification_type",
      "composer_tax_id",
    ];
    const modifiedData = requiredKeys?.reduce((acc, key) => {
      acc[key] = payload[key];
      return acc;
    }, {});
    const modifiedPayload = {
      ...modifiedData,
      phone_number: String(payload?.phone_number),
      npwp: payload?.npwp ? String(payload.npwp).replace(/[^0-9]/g, "") : "",
      composer_publisher: payload?.composer_publisher?.map(composer => ({
        ...composer,
        share_percentage: Number(composer?.share_percentage),
        contract_start_period: formatPayloadDate(
          composer?.contract_start_period
        ),
        contract_end_period: formatPayloadDate(composer?.contract_end_period),
      })),
      ...(id && {
        song_composer: payload?.song_composer?.map(
          ({ song_composer_id, composer_share_percentage }) => ({
            song_composer_id,
            composer_share_percentage,
          })
        ),
      }),
    };
    try {
      setLoadingPage(true);
      await method(
        `${hardBaseUrl}/publisher/composer${id ? `/${id}` : ""}`,
        modifiedPayload,
        { headers }
      );
      ModalSuccess(
        `Succesfully ${id ? "update" : "save"} composer/author`
      ).then(() => history.goBack());
    } catch (error) {
      const isConflict = error?.response?.data?.errors?.Detail?.is_conflict;
      ModalError(
        getErrors(error?.response),
        "Error",
        isConflict ? "Show Details" : "OK"
      ).then(() => {
        if (isConflict) {
          setModalTableVisible(true);
        }
      });
    } finally {
      setLoadingPage(false);
    }
  };
  const getErrorDetails = async () => {
    try {
      setLoadingTableModal(true);
      const res = await axios.get(
        `${hardBaseUrl}/composers/${id}/related-song`,
        {
          headers,
          params: tableModalParams,
        }
      );
      const { data, meta } = res?.data;

      setDataTableModal(data || []);
      const pageCount = getTotalPage(meta?.total, tableModalParams?.size);
      setTableModalTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingTableModal(false);
    }
  };
  const debounceErrorDetails = useCallback(
    debounce(async () => {
      setLoadingPage(true);
      await getErrorDetails();
      setLoadingPage(false);
    }, 500),
    [tableModalParams]
  );

  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    if (id && !isFirstLoad?.current) {
      getDataTable(payload?.sure_name);
    }
  }, [queryParams]);
  useEffect(() => {
    if (modalTableVisible) {
      const isSearching = Boolean(tableModalParams?.search);
      if (isSearching) {
        debounceErrorDetails();
        return () => {
          debounceErrorDetails.cancel();
        };
      } else {
        getErrorDetails();
      }
    }
  }, [modalTableVisible, tableModalParams, debounceErrorDetails]);

  return (
    <Page className={classes.root} title={pageTitle}>
      {loadingPage ? (
        <SkeletonComponent variant="wave" />
      ) : (
        <Container maxWidth={false}>
          <HeaderTitle
            title={pageTitle}
            breadcrumbData={breadcrumbData({ pageTitle })}
            backButton
          />
          <Divider className={classes?.divider} />
          <SectionLabel
            title="Account Information"
            subTitle="Fetaures to add composer/author's account"
          />
          <FormCard>
            <Grid container columnSpacing={2} rowSpacing={1}>
              {MockFieldFormAccountInformation({ optionTax }).map(
                ({
                  key,
                  title,
                  required,
                  type,
                  endAdorment,
                  options,
                  multiline,
                  rows,
                }) => (
                  <Grid item xs={12} md={key === "address" ? 12 : 6} key={key}>
                    <FormLabel label={title} required={required} />
                    {type === "autocomplete" ? (
                      <AutoCompleteComponent
                        placeholder={title}
                        options={options}
                        value={
                          options?.find(
                            option => option?.id === payload?.[key]
                          ) || null
                        }
                        onChange={e => handleChangePayload(e, key)}
                        size="small"
                        width="100%"
                      />
                    ) : type === "number" ? (
                      <NumberInput
                        placeholder={title}
                        autoComplete={`new-${key}`}
                        value={payload?.[key]}
                        onChange={val => handleChangePayload(val, key)}
                      />
                    ) : (
                      <TextInput
                        placeholder={key === "npwp" ? MASKS.NPWP : title}
                        value={payload?.[key]}
                        endAdornment={endAdorment}
                        onChange={e =>
                          handleChangePayload(
                            key === "npwp"
                              ? formatNpwp(e.target.value)
                              : e?.target?.value,
                            key
                          )
                        }
                        multiline={multiline}
                        rows={rows}
                      />
                    )}
                  </Grid>
                )
              )}
            </Grid>
          </FormCard>
          <Divider className={classes?.divider} />
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <SectionLabel title="Alias Name" />
            </Grid>
            <Grid item>
              <PrimaryButton
                label="Add Alias Name"
                startIcon={<Add />}
                onClick={() => {
                  setModalVisible(true);
                  setPreloadAliasName(null);
                }}
              />
            </Grid>
          </Grid>
          <CustomTable
            columnTable={[
              {
                name: "all",
                title: "Alias Name",
                renderText: item => item || "-",
              },
            ]}
            data={payload?.alias_names}
            isHaveAction
            deleteConfirmation
            deleteConfirmationKey="name"
            handleDelete={(_, index) => handleDeleteAliasName(index)}
            handleEdit={(item, index) => {
              setModalVisible(true);
              setPreloadAliasName({ name: item, index });
            }}
            mt="16px"
          />
          <Divider className={classes?.divider} />
          <SectionLabel
            title="Contract Information"
            subTitle="Fetaures to add composer/author's contract"
          />
          {id ? (
            <CustomTable
              columnTable={columnTableContractInformation}
              data={payload?.contracts}
              mt="16px"
            />
          ) : (
            <FormCard>
              <Grid container columnSpacing={2} rowSpacing={1}>
                {MockFieldFormContractInformation.map(
                  ({ key, title, type }) => (
                    <Grid item xs={12} md={type !== "date" ? 12 : 6} key={key}>
                      <FormLabel label={title} />
                      {type === "date" ? (
                        <DatePicker
                          value={payload?.composer_publisher?.[0]?.[key]}
                          onChange={
                            key === "contract_start_period"
                              ? handleChangeStartDate
                              : handleChangeEndDate
                          }
                        />
                      ) : (
                        <TextInput
                          placeholder={title}
                          value={payload?.composer_publisher?.[0]?.[key]}
                          onChange={e =>
                            handleChangeComposerPublisher(e?.target?.value, key)
                          }
                        />
                      )}
                    </Grid>
                  )
                )}
              </Grid>
            </FormCard>
          )}
          <Divider className={classes?.divider} />
          {id && (
            <Fragment>
              <SectionLabel
                title="Song Detail"
                subTitle="See song detail's of composer/author"
              />
              <CustomTable
                isLoading={loadingTable}
                columnTable={columnTableSongDetail({
                  handleChangeComposerPercentage,
                })}
                data={dataTable}
                page={queryParams?.page}
                pageSize={queryParams?.size}
                pageCount={tableTotalPage}
                handleChangePage={(_, page) =>
                  handleChangeQueryParams(page, "page")
                }
                handleChangePageSize={e =>
                  handleChangeQueryParams(e?.target?.value, "size")
                }
                isHaveAction
                handleEdit={item =>
                  history.push({
                    pathname: `/admin/parameter/lagu/edit/${item?.song_id}`,
                    state: {
                      customer: item,
                    },
                  })
                }
                skeletonHeight={758}
                mt="16px"
              />
              <Divider className={classes?.divider} />
            </Fragment>
          )}
          <Grid container justifyContent="right">
            <PrimaryButton label="Save" onClick={handleSubmit} />
          </Grid>
        </Container>
      )}
      {modalVisible && (
        <ModalComposerAliasName
          open={modalVisible}
          onClose={() => setModalVisible(false)}
          preload={preloadAliasName}
          onSubmit={handleAddAliasName}
        />
      )}
      {modalTableVisible && (
        <ModalTable
          open={modalTableVisible}
          onClose={() => setModalTableVisible(false)}
          title="Impacted Songs"
          isLoading={loadingTableModal}
          dataTable={dataTableModal}
          columnTable={columnModalTable}
          page={tableModalParams?.page}
          pageSize={tableModalParams?.size}
          pageCount={tableModalTotalPage}
          handleChangePage={(_, page) =>
            handleChangeTableModalParams(page, "page")
          }
          handleChangePageSize={e =>
            handleChangeTableModalParams(e.target.value, "size")
          }
          closeButton
          search
          searchValue={tableModalParams?.search}
          handleSearch={e =>
            handleChangeTableModalParams(e.target.value, "search")
          }
        />
      )}
    </Page>
  );
};

const breadcrumbData = ({ pageTitle }) => [
  {
    label: "Parameter",
    link: "/admin/parameter/pencipta",
  },
  {
    label: "Composer",
    link: "/admin/parameter/pencipta",
  },
  {
    label: pageTitle,
    active: true,
  },
];
const optionIdentificationType = [
  {
    id: "NIK",
    label: "NIK",
  },
  {
    id: "KITAS",
    label: "KITAS",
  },
  {
    id: "PASSPORT",
    label: "Passport",
  },
];
const MockFieldFormAccountInformation = ({ optionTax }) => [
  {
    title: "Composer/Author Name",
    key: "sure_name",
    required: true,
  },
  {
    title: "Phone Number",
    key: "phone_number",
    type: "number",
  },
  {
    title: "Email",
    key: "email",
  },
  {
    title: "Identification Number",
    key: "identification_number",
  },
  {
    title: "NPWP",
    key: "npwp",
  },
  {
    title: "Identification Type",
    key: "identification_type",
    options: optionIdentificationType,
    type: "autocomplete",
  },
  {
    title: "IPI Name Number",
    key: "ipi_name_number",
  },
  {
    title: "IPI Base Number",
    key: "ipi_base_number",
  },
  {
    title: "Tax",
    key: "composer_tax_id",
    options: optionTax,
    type: "autocomplete",
  },
  {
    title: "Address",
    key: "address",
    multiline: true,
    rows: 4,
  },
];
const MockFieldFormContractInformation = [
  {
    title: "Contract Number",
    key: "contract_number",
  },
  {
    title: "Start Date",
    key: "contract_start_period",
    type: "date",
  },
  {
    title: "End Date",
    key: "contract_end_period",
    type: "date",
  },
];
const columnTableContractInformation = [
  {
    name: "contract_number",
    title: "Contract Number",
    renderText: item => item || "-",
  },
  {
    name: "start_date",
    title: "Start Date",
    renderText: item => formatDate(moment(item, "DD-MM-YYYY hh:mm")),
  },
  {
    name: "end_date",
    title: "End Date",
    renderText: item =>
      item !== "-" ? formatDate(moment(item, "DD-MM-YYYY hh:mm")) : "-",
  },
  {
    name: "publisher_id",
    title: "Publisher Id",
    renderText: item => item || "-",
  },
  {
    name: "publisher_name",
    title: "Publisher",
    renderText: item => item || "-",
  },
  {
    name: "status",
    title: "Status",
    renderText: status => (
      <StatusChip
        label={status === "Active" ? "Active" : "Inactive"}
        type={status === "Active" ? "success" : ""}
      />
    ),
  },
];
const columnTableSongDetail = ({ handleChangeComposerPercentage }) => [
  {
    name: "song_title",
    title: "Song",
  },
  {
    name: "composer_name",
    title: "Composer/Author",
    renderText: item => item || "-",
  },
  {
    name: "all",
    title: "Composer Percentage",
    renderText: item => (
      <NumberInput
        value={item?.composer_share_percentage || 0}
        endAdornment="%"
        onChange={e => handleChangeComposerPercentage(e, item)}
      />
    ),
  },
  {
    name: "publisher_name",
    title: "Publisher",
    renderText: item => item || "-",
  },
  {
    name: "original_publisher_name",
    title: "Original Publisher",
    renderText: item => item || "-",
  },
];

const columnModalTable = [
  {
    name: "song_title",
    title: "Song",
  },
  {
    name: "publisher_name",
    title: "Publisher",
  },
  {
    name: "original_publisher_name",
    title: "Original Publisher",
  },
  {
    name: "percentage_ownership",
    title: "Composer Percentage",
    renderText: item => `${item || 0}%`,
  },
];
export default FormParameterComposer;
